import { cn } from "@/shared/lib/utils";
import {
	clampSurfaceLevel,
	SURFACE_BG_CLASSES,
	SURFACE_SHADOW_CLASSES,
	type SurfaceLevel,
} from "./surface-level";

/**
 * Returns the `bg-surface-N shadow-surface-M` className pair for a given
 * resolved level. Use inside primitives that wrap third-party components
 * (Base UI overlays etc.) and need to merge surface classes into an
 * existing className string without introducing a wrapping <div>.
 *
 * `opts.shadowLevel` decouples the shadow from the background level, per
 * the Fluid Functionalism rule "the shadow doesn't change, so a popover
 * still reads as a popover three layers down": the BACKGROUND expresses
 * depth (substrate + offset) while the SHADOW expresses the component's
 * IDENTITY (the recipe it has at base depth — see
 * `SURFACE_IDENTITY_SHADOWS` in surface-roles.ts). Omitted, the shadow
 * follows the background level (the spec's `shadowLevel ?? level`).
 *
 * Callers that own their DOM tree should prefer the `<Elevated/>`
 * component, which also provides the new substrate to descendants. After
 * calling this helper, callers MUST wrap the rendered subtree in
 * `<SurfaceProvider value={level}>` themselves so nested overlays step.
 */
export function getSurfaceClasses(
	level: SurfaceLevel,
	opts: {
		shadowOnly?: boolean;
		bgOnly?: boolean;
		foreground?: boolean;
		shadowLevel?: SurfaceLevel;
	} = {},
): string {
	warnOnShadowLevelDrift(level, opts.shadowLevel);
	return cn(
		!opts.shadowOnly && SURFACE_BG_CLASSES[level],
		!opts.bgOnly && SURFACE_SHADOW_CLASSES[opts.shadowLevel ?? level],
		opts.foreground && "text-surface-foreground",
	);
}

/**
 * Dev-only guardrail: a shadow may sit a step or two ABOVE its background
 * level (toast pins 8/4 the other way, which is fine), but a shadow more
 * than 2 levels HEAVIER than its background reads as a hovering black halo
 * with no surface to justify it. Never let `shadowLevel` exceed `level`
 * by more than 2.
 */
export function warnOnShadowLevelDrift(
	level: SurfaceLevel,
	shadowLevel: SurfaceLevel | undefined,
): void {
	if (
		import.meta.env.DEV &&
		shadowLevel !== undefined &&
		shadowLevel > level + 2
	) {
		// eslint-disable-next-line no-console -- dev-only contract warning
		console.warn(
			`[surface] shadowLevel ${shadowLevel} exceeds level ${level} by ` +
				"more than 2 — the shadow will read as detached. Pin the shadow " +
				"to the role identity (SURFACE_IDENTITY_SHADOWS) instead.",
		);
	}
}

/** Resolve `substrate + offset` clamped to [1, 8]. Tiny helper for sites
 * that read `useSurface()` and need to compute their own level. */
export function resolveSurfaceLevel(
	substrate: SurfaceLevel,
	offset: number,
): SurfaceLevel {
	return clampSurfaceLevel(substrate + offset);
}

/* ─── Flush surfaces (viewport-pinned containers) ───────────────────────
 * A Sheet or Drawer slides in pinned against a screen edge. Its shadow
 * must cast toward the INNER edge (the content-facing side) only — an
 * omnidirectional `shadow-surface-N` wastes half the recipe off-viewport
 * and reads wrong at the pinned edge. In dark mode the same inner edge
 * carries a single-edge rim via `data-surface-rim="<innerEdge>"` (see
 * surfaces.css). */

/** The four edges; used both for "which screen edge is the container
 * attached to" and "which edge faces the content". */
export type SurfaceFlushEdge = "top" | "bottom" | "left" | "right";

/** Map a container's ATTACH side (the screen edge it is pinned to) to its
 * INNER edge (the side facing the page content, where shadow + rim go):
 * right-attached → inner edge left, bottom-attached → inner edge top. */
export const INNER_EDGE_FROM_ATTACH_SIDE: Record<
	SurfaceFlushEdge,
	SurfaceFlushEdge
> = {
	top: "bottom",
	bottom: "top",
	left: "right",
	right: "left",
};

export const SURFACE_FLUSH_SHADOW_CLASSES: Record<SurfaceFlushEdge, string> = {
	top: "shadow-surface-flush-top",
	bottom: "shadow-surface-flush-bottom",
	left: "shadow-surface-flush-left",
	right: "shadow-surface-flush-right",
};

/**
 * `bg-surface-N` + the directional flush shadow toward `innerEdge`.
 * Callers stamp `data-surface-level` + `data-surface-rim={innerEdge}`
 * themselves and wrap the subtree in `<SurfaceProvider value={level}>`,
 * exactly like `getSurfaceClasses` callers. The flush shadow is fixed at
 * the modal identity weight (flush containers are modal-class overlays),
 * so there is no `shadowLevel` knob here.
 */
export function getFlushSurfaceClasses(
	level: SurfaceLevel,
	innerEdge: SurfaceFlushEdge,
	opts: { bgOnly?: boolean; foreground?: boolean } = {},
): string {
	return cn(
		SURFACE_BG_CLASSES[level],
		!opts.bgOnly && SURFACE_FLUSH_SHADOW_CLASSES[innerEdge],
		opts.foreground && "text-surface-foreground",
	);
}
